import { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { ScrollArea } from '../../../components/ui/scroll-area';
import { Alert, AlertDescription } from '../../../components/ui/alert';
import { AlertCircle, HelpCircle, Info } from 'lucide-react';
import { Spinner } from '../../../components/ui/spinner';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../../components/ui/tooltip';
import { cn } from '../../../lib/utils';
import type { EpicWithCalculations } from '../../../api/shared/types';
import { saveBulkEpicOverrides, type BulkEpicOverrideItem, type EpicOverridePayload } from '../../../lib/epic-details';
import { triggerCapexEvaluation, triggerRndEvaluation, updateProjectOverride, pollEvaluationJobUntilDone } from '../../../api/project-evaluations';
import { authenticatedFetch } from '../../../api/shared/authenticatedFetch';
import { useProductSettings } from '@workspace/utils-react';
import type { Settings } from '../../../AppContainer';

interface CalculationBreakdownModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  type: 'capex' | 'rnd';
  epics: EpicWithCalculations[];
  reviewId?: string;
  onEpicClick?: (epic: EpicWithCalculations) => void;
  onTotalChange?: (total: number) => void;
  onUpdate?: () => void;
  fiscalYearQuarter?: string;
  areaLevel1?: string;
}

interface CellSelection {
  epicId: string;
  percentageType: 'ado-jira' | 'claude' | 'manual';
  sizeType: 'duration' | 'manual';
}

export function CalculationBreakdownModal({
  open,
  onOpenChange,
  type,
  epics,
  reviewId: _reviewId,
  onEpicClick,
  onTotalChange,
  onUpdate,
  fiscalYearQuarter,
  areaLevel1,
}: CalculationBreakdownModalProps) {
  const { apiUrl } = useProductSettings<Settings>();
  const [showExplainer, setShowExplainer] = useState(false);
    const [selections, setSelections] = useState<Map<string, CellSelection>>(new Map());
    const [manualSizes, setManualSizes] = useState<Map<string, number>>(new Map());
    const [manualPercentages, setManualPercentages] = useState<Map<string, number>>(new Map());
  const [isSaving, setIsSaving] = useState(false);
  const [jobState, setJobState] = useState<'idle' | 'running' | 'failed'>('idle');
  const [jobError, setJobError] = useState<string | null>(null);

    const initialSelectionsRef = useRef<Map<string, CellSelection>>(new Map());
    const initialManualSizesRef = useRef<Map<string, number>>(new Map());
    const initialManualPercentagesRef = useRef<Map<string, number>>(new Map());
    const initializedEpicsRef = useRef<Set<string>>(new Set());
    const epicSourceCacheRef = useRef<Map<string, string>>(new Map());
  const snapshotTakenRef = useRef(false);

  const title = type === 'capex' ? 'CapEx Calculation Breakdown' : 'R&D Calculation Breakdown';

  const takeSnapshot = useCallback(() => {
    if (!snapshotTakenRef.current && selections.size > 0) {
      initialSelectionsRef.current = new Map(selections);
      initialManualSizesRef.current = new Map(manualSizes);
      initialManualPercentagesRef.current = new Map(manualPercentages);
      snapshotTakenRef.current = true;
    }
  }, [selections, manualSizes, manualPercentages]);

  useEffect(() => {
    if (open && selections.size > 0 && !snapshotTakenRef.current) {
      takeSnapshot();
    }
    if (!open) {
      snapshotTakenRef.current = false;
    }
  }, [open, selections, takeSnapshot]);

  // Get the relevant percentage field based on type
  const getPercentage = useCallback(
    (epic: EpicWithCalculations) => {
      return type === 'capex' ? epic.activeCapExPercentage || 0 : epic.activeRnDPercentage || 0;
    },
    [type],
  );

  // Get the percentage source
  const getPercentageSource = useCallback(
    (epic: EpicWithCalculations) => {
      return type === 'capex' ? epic.capExSource : epic.rndSource;
    },
    [type],
  );

  // Get the Claude-assessed percentage (not the active percentage)
  const getClaudePercentage = useCallback(
    (epic: EpicWithCalculations) => {
      return type === 'capex' ? epic.claudeCapExPercentage || 0 : epic.claudeRnDPercentage || 0;
    },
    [type],
  );

  // Filter out any invalid epics
  const validEpics = useMemo(() => {
    return epics.filter((epic) => epic && typeof epic === 'object');
  }, [epics]);

  // Determine if we're using ADO or JIRA
  const integrationSystem = useMemo(() => {
    const firstEpicWithSource = validEpics.find((epic: EpicWithCalculations) => epic.data_source);
    if (firstEpicWithSource?.data_source?.toLowerCase().includes('jira')) {
      return 'JIRA';
    }
    return 'ADO';
  }, [validEpics]);


  // Initialize selections for NEW epics only (don't overwrite user selections)
  // Also initialize manualPercentages and manualSizes from epic data when source is Manual
  // Using a ref to track initialized epics to avoid cascading renders from setState in effect
  useEffect(() => {
    // Find epics that haven't been initialized yet
    const uninitialized = validEpics.filter(
      (epic: EpicWithCalculations) => !initializedEpicsRef.current.has(epic.id)
    );

    if (uninitialized.length === 0) return;

    // Batch all state updates together using functional updates
    setSelections((prevSelections: Map<string, CellSelection>) => {
      const newSelections = new Map(prevSelections);
      uninitialized.forEach((epic: EpicWithCalculations) => {
        // Determine percentage type based on current epic source
        let percentageType: CellSelection['percentageType'] = 'ado-jira';
        const source = getPercentageSource(epic);
        if (source === 'C') percentageType = 'claude';
        else if (source === 'M') percentageType = 'manual';

        // Determine size type based on current epic estimate source
        const sizeType: CellSelection['sizeType'] = epic.estimateSource === 'M' ? 'manual' : 'duration';

        newSelections.set(epic.id, {
          epicId: epic.id,
          percentageType,
          sizeType,
        });
      });
      return newSelections;
    });

    setManualPercentages((prevManualPercentages: Map<string, number>) => {
      const newManualPercentages = new Map(prevManualPercentages);
      uninitialized.forEach((epic: EpicWithCalculations) => {
        const source = getPercentageSource(epic);
        if (source === 'M') {
          const activePercentage = getPercentage(epic);
          newManualPercentages.set(epic.id, activePercentage);
        }
      });
      return newManualPercentages;
    });

    setManualSizes((prevManualSizes: Map<string, number>) => {
      const newManualSizes = new Map(prevManualSizes);
      uninitialized.forEach((epic: EpicWithCalculations) => {
        if (epic.estimateSource === 'M') {
          newManualSizes.set(epic.id, epic.estimate);
        }
      });
      return newManualSizes;
    });

    // Mark these epics as initialized and cache their source state
    uninitialized.forEach((epic: EpicWithCalculations) => {
      initializedEpicsRef.current.add(epic.id);
      const percentageSource = getPercentageSource(epic);
      epicSourceCacheRef.current.set(
        epic.id,
        `${percentageSource}|${epic.estimateSource}|${getPercentage(epic)}|${epic.estimate}`,
      );
    });
  }, [validEpics, getPercentageSource, getPercentage]);

  // Sync selections when epic sources change externally (e.g., from EpicDetailModal)
  // Skip syncing while modal is open to preserve user's in-progress edits
  /* eslint-disable react-hooks/set-state-in-effect -- intentional sync of derived state from props */
  useEffect(() => {
    if (open) return;

        const selectionsToUpdate = new Map<string, CellSelection>();
        const manualPercentagesToUpdate = new Map<string, number>();
        const manualSizesToUpdate = new Map<string, number>();
        const manualSizesToClear = new Set<string>();

    validEpics.forEach((epic: EpicWithCalculations) => {
      if (!initializedEpicsRef.current.has(epic.id)) return;

      const percentageSource = getPercentageSource(epic);
      const cacheKey = `${percentageSource}|${epic.estimateSource}|${getPercentage(epic)}|${epic.estimate}`;

      if (epicSourceCacheRef.current.get(epic.id) === cacheKey) return;
      epicSourceCacheRef.current.set(epic.id, cacheKey);

      let percentageType: CellSelection['percentageType'] = 'ado-jira';
      if (percentageSource === 'C') percentageType = 'claude';
      else if (percentageSource === 'M') percentageType = 'manual';

      const sizeType: CellSelection['sizeType'] = epic.estimateSource === 'M' ? 'manual' : 'duration';
      selectionsToUpdate.set(epic.id, { epicId: epic.id, percentageType, sizeType });

      if (percentageSource === 'M') {
        manualPercentagesToUpdate.set(epic.id, getPercentage(epic));
      }
      if (epic.estimateSource === 'M') {
        manualSizesToUpdate.set(epic.id, epic.estimate);
      } else {
        // Clear manual size when switching back to duration
        manualSizesToClear.add(epic.id);
      }
    });

    if (selectionsToUpdate.size === 0) return;

        setSelections((prev: Map<string, CellSelection>) => {
          const updated = new Map(prev);
          selectionsToUpdate.forEach((v, k) => updated.set(k, v));
          return updated;
        });

        if (manualPercentagesToUpdate.size > 0) {
          setManualPercentages((prev: Map<string, number>) => {
        const updated = new Map(prev);
        manualPercentagesToUpdate.forEach((v, k) => updated.set(k, v));
        return updated;
      });
    }

    if (manualSizesToUpdate.size > 0 || manualSizesToClear.size > 0) {
            setManualSizes((prev: Map<string, number>) => {
              const updated = new Map(prev);
              manualSizesToUpdate.forEach((v, k) => updated.set(k, v));
              manualSizesToClear.forEach((k) => updated.delete(k));
              return updated;
            });
    }
  }, [validEpics, getPercentageSource, getPercentage, open]);


  // Get the active size value for an epic based on explicit size selection
  const getActiveSize = useCallback(
    (epic: EpicWithCalculations): number => {
      // Special case: if only one EPIC, set size to 1
      if (validEpics.length === 1) {
        return 1;
      }

      const selection = selections.get(epic.id);
      const sizeType = selection?.sizeType ?? 'duration';

      if (sizeType === 'manual') {
        const manualOverride = manualSizes.get(epic.id);
        if (manualOverride && manualOverride > 0) return manualOverride;
      }

      // Fall back to duration (days) from the API — consistent with EpicDetailModal
      if (epic.durationDays > 0) {
        return epic.durationDays;
      }
      return epic.estimate;
    },
    [validEpics, manualSizes, selections],
  );

  // Get the active percentage value for an epic based on the selected percentage type
  const getActivePercentage = useCallback(
    (epic: EpicWithCalculations): number => {
      const selection = selections.get(epic.id);
      if (!selection) return getPercentage(epic);

      switch (selection.percentageType) {
        case 'ado-jira':
          // Use ADO/JIRA percentage if available
          return type === 'capex' ? (epic.capex_percentage ?? 0) : (epic.r_and_d_percentage ?? 0);
        case 'claude':
          // Use Claude-assessed percentage
          return getClaudePercentage(epic);
        case 'manual': {
          // Use manually entered percentage, clamped to 0-100
          const manualValue = manualPercentages.get(epic.id) ?? 0;
          return Math.min(100, Math.max(0, manualValue));
        }
        default:
          return getPercentage(epic);
      }
    },
    [selections, manualPercentages, type, getPercentage, getClaudePercentage],
  );

  // Calculate totals for the summary row
  const totals = useMemo(() => {
    const totalSize = validEpics.reduce((sum: number, epic: EpicWithCalculations) => sum + getActiveSize(epic), 0);
    
    const totalContribution = validEpics.reduce((sum: number, epic: EpicWithCalculations) => {
      const size = getActiveSize(epic);
      const percentage = getActivePercentage(epic);
      return sum + (size * percentage) / 100;
    }, 0);

    const averagePercentage = totalSize > 0 ? (totalContribution / totalSize) * 100 : 0;

    return {
      totalSize,
      averagePercentage,
      totalContribution,
    };
  }, [validEpics, getActiveSize, getActivePercentage]);

  // Calculate total weighted percentage (for backward compatibility)
  const total = totals.averagePercentage;

  // Calculate contribution for an EPIC
  const calculateContribution = (epic: EpicWithCalculations) => {
    const size = getActiveSize(epic);
    const percentage = getActivePercentage(epic);
    return (size * percentage) / 100;
  };

    const handlePercentageSelection = useCallback((epicId: string, percentageType: CellSelection['percentageType']) => {
      setSelections((prev: Map<string, CellSelection>) => {
      const newSelections = new Map(prev);
      const current = newSelections.get(epicId);
      if (current) {
        newSelections.set(epicId, { ...current, percentageType });
      }
      return newSelections;
    });
  }, []);

    const handleSizeSelection = useCallback((epicId: string, sizeType: CellSelection['sizeType']) => {
      setSelections((prev: Map<string, CellSelection>) => {
      const newSelections = new Map(prev);
      const current = newSelections.get(epicId);
      if (current) {
        newSelections.set(epicId, { ...current, sizeType });
      }
      return newSelections;
    });
  }, []);

  const buildOverridePayload = useCallback(
    (epicItem: EpicWithCalculations, overrides?: {
      capexPercentage?: number;
      rndPercentage?: number;
      selectedPercentage?: string;
      capexSource?: string;
      rndSource?: string;
      epicSize?: string;
      manualEstimateValue?: number | null;
    }): EpicOverridePayload => {
      const manualOverride = manualSizes.get(epicItem.id);
      const selection = selections.get(epicItem.id);
      const currentSizeType = selection?.sizeType ?? 'duration';
      const hasManualSize = manualOverride !== undefined && manualOverride > 0;
      const epicSize = overrides?.epicSize ?? (currentSizeType === 'manual' && hasManualSize ? 'manual' : 'duration');
      const manualEstimateValue = epicSize === 'manual'
        ? (overrides?.manualEstimateValue ?? manualOverride ?? null)
        : null;
      return {
        manualCapexPercentage: overrides?.capexPercentage ?? epicItem.activeCapExPercentage,
        manualRndPercentage: overrides?.rndPercentage ?? epicItem.activeRnDPercentage,
        selectedPercentage: overrides?.selectedPercentage ?? (type === 'capex' ? epicItem.capExSource : epicItem.rndSource),
        capexSource: overrides?.capexSource,
        rndSource: overrides?.rndSource,
        epicSize,
        manualEstimateValue,
        status: 'active',
      };
    },
    [type, manualSizes, selections],
  );

    const handleManualPercentageChange = useCallback(
      (epicId: string, value: number) => {
        setManualPercentages((prev: Map<string, number>) => new Map(prev).set(epicId, value));
    },
    [],
  );

    const handleManualSizeChange = useCallback(
      (epicId: string, value: number) => {
        setManualSizes((prev: Map<string, number>) => new Map(prev).set(epicId, value));
    },
    [],
  );

  const handleDiscard = useCallback(() => {
    setSelections(new Map(initialSelectionsRef.current));
    setManualSizes(new Map(initialManualSizesRef.current));
    setManualPercentages(new Map(initialManualPercentagesRef.current));
    snapshotTakenRef.current = false;
    onOpenChange(false);
  }, [onOpenChange]);

  const runEvaluationJob = useCallback(async () => {
    if (!fiscalYearQuarter) {
      onOpenChange(false);
      onUpdate?.();
      return;
    }

    setJobError(null);
    setJobState('running');
    try {
      const jobResponse = type === 'capex'
        ? await triggerCapexEvaluation(apiUrl, fiscalYearQuarter, areaLevel1)
        : await triggerRndEvaluation(apiUrl, fiscalYearQuarter, areaLevel1);

      const result = await pollEvaluationJobUntilDone(apiUrl, jobResponse.job_id);

      if (result.status !== 'completed') {
        setJobError(result.error_message ?? 'The re-evaluation job failed. Please try again.');
        setJobState('failed');
        return;
      }

      setJobState('idle');
      onOpenChange(false);
      // Defer the data reload so the API has time to propagate saved overrides
      // before loadData refetches and recalculates weighted averages
      setTimeout(() => {
        onUpdate?.();
      }, 500);
    } catch (error) {
      console.error(`ProjectApproval_ Failed to complete ${type} project evaluation:`, error);
      setJobError(error instanceof Error ? error.message : 'The re-evaluation job failed. Please try again.');
      setJobState('failed');
    }
  }, [apiUrl, areaLevel1, fiscalYearQuarter, onOpenChange, onUpdate, type]);

  useEffect(() => {
    if (jobState !== 'running') return;

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [jobState]);

  const handleSave = useCallback(async () => {
    setIsSaving(true);
    try {
      const bulkOverrides: BulkEpicOverrideItem[] = [];

      for (const epic of validEpics) {
        const selection = selections.get(epic.id);
        if (!selection) continue;

        const overrides: {
          capexPercentage?: number;
          rndPercentage?: number;
          selectedPercentage?: string;
          capexSource?: string;
          rndSource?: string;
          epicSize?: string;
          manualEstimateValue?: number | null;
        } = {};

        if (selection.percentageType === 'manual') {
          const value = manualPercentages.get(epic.id) ?? 0;
          if (type === 'capex') {
            overrides.capexPercentage = value;
            overrides.capexSource = 'manual';
          } else {
            overrides.rndPercentage = value;
            overrides.rndSource = 'manual';
          }
          overrides.selectedPercentage = 'manual';
        } else if (selection.percentageType === 'ado-jira') {
          overrides.selectedPercentage = 'original';
          if (type === 'capex') {
            overrides.capexSource = 'alm';
          } else {
            overrides.rndSource = 'alm';
          }
        } else if (selection.percentageType === 'claude') {
          overrides.selectedPercentage = 'claude';
          if (type === 'capex') {
            overrides.capexSource = 'claude';
          } else {
            overrides.rndSource = 'claude';
          }
        }

        const epicSizeType = selection.sizeType ?? 'duration';
        const manualValue = manualSizes.get(epic.id);
        if (epicSizeType === 'manual' && manualValue !== undefined && manualValue > 0) {
          overrides.epicSize = 'manual';
          overrides.manualEstimateValue = manualValue;
        } else {
          overrides.epicSize = 'duration';
        }

        const payload = buildOverridePayload(epic, overrides);
        bulkOverrides.push({
          epicId: String(epic.id),
          ...payload,
        });
      }

      if (bulkOverrides.length > 0) {
        await saveBulkEpicOverrides(apiUrl, bulkOverrides);
      }

      onTotalChange?.(total);
      snapshotTakenRef.current = false;

      // Save project-level override with the new calculated totals
      // Only update the percentage for the current type, preserving the other type's value
      if (areaLevel1 && fiscalYearQuarter) {
        try {
          // Fetch existing override first to preserve the other type's percentage
          let existingCapex = 0;
          let existingRnd = 0;
          try {
            const existingOverride = await authenticatedFetch(
              `${apiUrl}/project-evaluations/overrides?areaLevel1=${encodeURIComponent(areaLevel1)}&fiscalYearQuarter=${encodeURIComponent(fiscalYearQuarter)}`
            );
            const existing = await existingOverride.json();
            existingCapex = existing.manual_capex_percentage ?? 0;
            existingRnd = existing.manual_rnd_percentage ?? 0;
          } catch {
            // No existing override found, use 0 defaults
          }

          const payload: {
            status: string;
            manual_capex_percentage: number;
            manual_rnd_percentage: number;
            selected_value: string;
          } = {
            status: 'active',
            manual_capex_percentage: type === 'capex' ? Math.round(total) : existingCapex,
            manual_rnd_percentage: type === 'rnd' ? Math.round(total) : existingRnd,
            selected_value: 'Manual',
          };

          await updateProjectOverride(apiUrl, areaLevel1, fiscalYearQuarter, payload);
        } catch (overrideError) {
          console.warn('ProjectApproval_ Failed to save project override:', overrideError);
        }
      }

      setIsSaving(false);
      await runEvaluationJob();
    } catch (error) {
      console.error('ProjectApproval_ Error saving changes:', error);
      setIsSaving(false);
    }
  }, [validEpics, selections, manualPercentages, type, buildOverridePayload, apiUrl, onTotalChange, total, fiscalYearQuarter, areaLevel1, manualSizes, runEvaluationJob]);

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="!max-w-[95vw] w-fit max-h-[90vh] flex flex-col sm:!max-w-[95vw]" showCloseButton={false} onInteractOutside={(e: Event) => e.preventDefault()} onEscapeKeyDown={(e: KeyboardEvent) => e.preventDefault()}>
          <DialogHeader>
            <div className="flex items-center justify-between w-[99%]">
              <div>
                <DialogTitle>{title}</DialogTitle>
                <DialogDescription>
                  Detailed breakdown showing how each EPIC contributes to the overall{' '}
                  {type === 'capex' ? 'CapEx' : 'R&D'} percentage
                </DialogDescription>
              </div>
              <Button variant="outline" size="sm" onClick={() => setShowExplainer(true)} className="gap-2">
                <HelpCircle className="h-4 w-4" />
                Explainer
              </Button>
            </div>
          </DialogHeader>

          {jobState === 'running' && (
            <div className="flex flex-col items-center justify-center gap-3 border border-primary/20 bg-primary/5 rounded-lg p-6">
              <Spinner className="h-6 w-6" />
              <p className="text-sm font-medium">Recalculating — please do not close this tab…</p>
            </div>
          )}

          {jobState === 'failed' && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription className="flex items-center justify-between gap-4 w-full">
                <span>{jobError ?? 'The re-evaluation job failed.'}</span>
                <Button variant="outline" size="sm" onClick={runEvaluationJob}>Retry</Button>
              </AlertDescription>
            </Alert>
          )}

          {/* Total Display */}
          <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 mb-4">
            <div className="text-center">
              <div className="text-sm text-muted-foreground mb-1">Total {type === 'capex' ? 'CapEx' : 'R&D'}</div>
              <div className="text-4xl font-bold text-primary">{total.toFixed(0)}%</div>
            </div>
          </div>

          {/* Table */}
          <div className="flex-1 overflow-auto -mx-6 px-6">
            {validEpics.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">No EPICs available to display</div>
            ) : (
              <div className="border rounded-lg overflow-hidden inline-block min-w-full">
                <TooltipProvider delayDuration={0}>
                <table className="w-auto border-collapse">
                  <thead className="bg-muted/50 sticky top-0 z-10">
                    <tr>
                      <th rowSpan={2} className="text-center p-3 font-medium text-sm border-r border-b min-w-[250px]">
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="underline decoration-dotted">EPIC</span>
                          </TooltipTrigger>
                          <TooltipContent className="bg-white text-foreground border shadow-md" hideArrow>Click on an EPIC to view details</TooltipContent>
                        </Tooltip>
                      </th>
                      <th
                        colSpan={2}
                        className="text-center p-3 font-medium text-sm border-r border-b"
                      >
                        Relative EPIC Size
                      </th>
                      <th rowSpan={2} className="text-center p-3 font-medium text-sm border-r border-b w-[50px]">
                        ×
                      </th>
                      <th colSpan={3} className="text-center p-3 font-medium text-sm border-r border-b">
                        Percentage
                      </th>
                      <th rowSpan={2} className="text-center p-3 font-medium text-sm border-r border-b w-[50px]">
                        =
                      </th>
                      <th rowSpan={2} className="text-center p-3 font-medium text-sm border-b min-w-[120px]">
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="underline decoration-dotted">Contribution</span>
                          </TooltipTrigger>
                          <TooltipContent className="bg-white text-foreground border shadow-md" hideArrow>(Size × Percentage) ÷ 100</TooltipContent>
                        </Tooltip>
                      </th>
                    </tr>
                    <tr>
                      {/* Size sub-columns */}
                      <th className="text-center p-2 text-xs font-medium border-r border-b bg-muted/30 min-w-[120px]">
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="underline decoration-dotted">Duration (days)</span>
                          </TooltipTrigger>
                          <TooltipContent className="bg-white text-foreground border shadow-md" hideArrow>Days between Active and Closed dates (duration_created_to_closed)</TooltipContent>
                        </Tooltip>
                      </th>
                      <th className="text-center p-2 text-xs font-medium border-r border-b bg-muted/30 min-w-[100px]">
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="underline decoration-dotted">Manual Override</span>
                          </TooltipTrigger>
                          <TooltipContent className="bg-white text-foreground border shadow-md" hideArrow>Enter a custom size value to override duration</TooltipContent>
                        </Tooltip>
                      </th>

                      {/* Percentage sub-columns */}
                      <th className="text-center p-2 text-xs font-medium border-r border-b bg-muted/30 min-w-[100px]">
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="underline decoration-dotted">{integrationSystem}</span>
                          </TooltipTrigger>
                          <TooltipContent className="bg-white text-foreground border shadow-md" hideArrow>{type === 'capex' ? 'CapEx' : 'R&D'} % from {integrationSystem}</TooltipContent>
                        </Tooltip>
                      </th>
                      <th className="text-center p-2 text-xs font-medium border-r border-b bg-muted/30 min-w-[100px]">
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="underline decoration-dotted">System Recommended</span>
                          </TooltipTrigger>
                          <TooltipContent className="bg-white text-foreground border shadow-md" hideArrow>System recommended {type === 'capex' ? 'CapEx' : 'R&D'} %</TooltipContent>
                        </Tooltip>
                      </th>
                      <th className="text-center p-2 text-xs font-medium border-r border-b bg-muted/30 min-w-[100px]">
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="underline decoration-dotted">Manual Override</span>
                          </TooltipTrigger>
                          <TooltipContent className="bg-white text-foreground border shadow-md" hideArrow>Enter custom percentage (0-100)</TooltipContent>
                        </Tooltip>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {validEpics.map((epic: EpicWithCalculations) => {
                      const selection = selections.get(epic.id);
                      const contribution = calculateContribution(epic);
                      const hasAdoJiraPercentage = epic.capex_percentage !== null && epic.r_and_d_percentage !== null;
                      const adoJiraPercentage = hasAdoJiraPercentage
                        ? type === 'capex'
                          ? epic.capex_percentage
                          : epic.r_and_d_percentage
                        : null;
                      const manualOverride = manualSizes.get(epic.id);
                      const hasManualOverride = manualOverride !== undefined && manualOverride > 0;
                      const sizeType = selection?.sizeType ?? 'duration';
                      const manualPctValue = manualPercentages.get(epic.id);
                      const hasManualPercentage = manualPctValue !== undefined && manualPctValue > 0;

                      return (
                        <tr key={epic.id} className="border-t hover:bg-muted/30 transition-colors">
                          {/* EPIC Column */}
                          <td className="p-3 border-r min-w-[250px]">
                            <button
                              onClick={() => onEpicClick?.(epic)}
                              className="text-left hover:underline text-primary font-medium w-full"
                            >
                              <div className="font-mono text-sm">EPIC-{epic.id}</div>
                              <div className="text-sm text-muted-foreground line-clamp-2">{epic.title}</div>
                            </button>
                          </td>

                          {/* Duration (days) - clickable, selects duration as size source */}
                          <td
                            className={cn(
                              'p-3 border-r text-center cursor-pointer transition-colors',
                              sizeType === 'duration' && 'bg-green-100 hover:bg-green-200',
                              sizeType !== 'duration' && 'hover:bg-muted/50',
                            )}
                            onClick={() => handleSizeSelection(epic.id, 'duration')}
                          >
                            <div className="font-semibold">
                              {validEpics.length === 1 ? '1.0' : epic.durationDays.toFixed(1)}
                            </div>
                          </td>

                          {/* Manual Override */}
                          <td
                            className={cn(
                              'p-3 border-r text-center cursor-pointer transition-colors',
                              sizeType === 'manual' && hasManualOverride && 'bg-amber-100 hover:bg-amber-200',
                              sizeType === 'manual' && !hasManualOverride && 'bg-green-100 hover:bg-green-200',
                              sizeType !== 'manual' && 'hover:bg-muted/50',
                            )}
                            onClick={() => handleSizeSelection(epic.id, 'manual')}
                          >
                            <Input
                              type="number"
                              step="0.1"
                              min="0"
                              value={manualSizes.get(epic.id) || ''}
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                const rawValue = e.target.value;
                                if (rawValue === '') {
                                                                    setManualSizes((prev: Map<string, number>) => {
                                                                      const newMap = new Map(prev);
                                                                      newMap.delete(epic.id);
                                                                      return newMap;
                                                                    });
                                  handleSizeSelection(epic.id, 'duration');
                                  return;
                                }
                                const value = parseFloat(rawValue);
                                if (!isNaN(value)) {
                                  handleManualSizeChange(epic.id, value);
                                  handleSizeSelection(epic.id, 'manual');
                                }
                              }}
                              onFocus={() => handleSizeSelection(epic.id, 'manual')}
                              onClick={(e: React.MouseEvent<HTMLInputElement>) => e.stopPropagation()}
                              className="w-20 text-center"
                              placeholder="—"
                            />
                          </td>

                          {/* Multiplication Sign */}
                          <td className="p-3 border-r text-center text-2xl text-muted-foreground">×</td>

                          {/* Percentage Columns */}
                          {/* ADO/JIRA Percentage */}
                          <td
                            className={cn(
                              'p-3 border-r text-center cursor-pointer transition-colors',
                              selection?.percentageType === 'ado-jira' && 'bg-green-100 hover:bg-green-200',
                              selection?.percentageType !== 'ado-jira' && 'hover:bg-muted/50',
                            )}
                            onClick={() => hasAdoJiraPercentage && handlePercentageSelection(epic.id, 'ado-jira')}
                          >
                            {hasAdoJiraPercentage && adoJiraPercentage !== null ? (
                              <div className="font-semibold">{adoJiraPercentage.toFixed(0)}%</div>
                            ) : (
                              <div className="text-muted-foreground">--</div>
                            )}
                          </td>

                          {/* Claude Percentage */}
                          <td
                            className={cn(
                              'p-3 border-r text-center cursor-pointer transition-colors',
                              selection?.percentageType === 'claude' && 'bg-green-100 hover:bg-green-200',
                              selection?.percentageType !== 'claude' && 'hover:bg-muted/50',
                            )}
                            onClick={() => handlePercentageSelection(epic.id, 'claude')}
                          >
                            <div className="font-semibold">{getClaudePercentage(epic).toFixed(0)}%</div>
                          </td>

                          {/* Manual Percentage */}
                          <td
                            className={cn(
                              'p-3 border-r text-center cursor-pointer transition-colors',
                              selection?.percentageType === 'manual' && hasManualPercentage && 'bg-amber-100 hover:bg-amber-200',
                              selection?.percentageType === 'manual' && !hasManualPercentage && 'bg-green-100 hover:bg-green-200',
                              selection?.percentageType !== 'manual' && 'hover:bg-muted/50',
                            )}
                            onClick={() => handlePercentageSelection(epic.id, 'manual')}
                          >
                            <Input
                              type="number"
                              step="0.1"
                              min="0"
                              max="100"
                              value={manualPercentages.get(epic.id) ?? ''}
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                const rawValue = e.target.value;
                                if (rawValue === '') {
                                                                    setManualPercentages((prev: Map<string, number>) => {
                                                                      const newMap = new Map(prev);
                                                                      newMap.delete(epic.id);
                                                                      return newMap;
                                                                    });
                                  return;
                                }
                                const value = parseFloat(rawValue);
                                if (!isNaN(value)) {
                                  const clampedValue = Math.min(100, Math.max(0, value));
                                  handleManualPercentageChange(epic.id, clampedValue);
                                }
                              }}
                              onFocus={() => handlePercentageSelection(epic.id, 'manual')}
                              onClick={(e: React.MouseEvent<HTMLInputElement>) => {
                                e.stopPropagation();
                                handlePercentageSelection(epic.id, 'manual');
                              }}
                              className="w-20 text-center"
                              placeholder="0.0"
                            />
                          </td>

                          {/* Equals Sign */}
                          <td className="p-3 border-r text-center text-2xl text-muted-foreground">=</td>

                          {/* Contribution Column */}
                          <td className="p-3 text-center">
                            <div className="font-bold text-xl text-primary">{contribution.toFixed(2)}</div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot className="bg-muted/50 sticky bottom-0">
                    <tr className="border-t-2 border-primary/30 font-semibold">
                      <td className="p-3 border-r text-right">Total</td>
                      <td colSpan={2} className="p-3 border-r text-center">
                        {totals.totalSize.toFixed(1)}
                      </td>
                      <td className="p-3 border-r text-center text-2xl text-muted-foreground">×</td>
                      <td colSpan={3} className="p-3 border-r text-center">
                        {totals.averagePercentage.toFixed(0)}%
                      </td>
                      <td className="p-3 border-r text-center text-2xl text-muted-foreground">=</td>
                      <td className="p-3 text-center">
                        <div className="font-bold text-xl text-primary">{totals.averagePercentage.toFixed(0)}%</div>
                      </td>
                    </tr>
                  </tfoot>
                </table>
                </TooltipProvider>
              </div>
            )}
          </div>

          {/* Footer */}
          <DialogFooter className="border-t pt-4 mt-4">
            <div className="flex items-center justify-between w-full">
              {/* Information Banner */}
              <Alert className="border-blue-200 bg-blue-50 text-xs px-3 py-2 mx-1 my-0 flex-1 min-w-0">
                <Info className="h-4 w-4 text-blue-600" />
                <AlertDescription className="text-blue-800">
                  Click Duration (days) or Manual Override to select the active size source for each EPIC. Click a Percentage
                  column to select the active percentage. Selected cells are highlighted in green. Manually entered values
                  (Manual Override size or Manual percentage) turn amber when active. The total {type === 'capex' ? 'CapEx' : 'R&D'} percentage
                  is calculated as a weighted average based on the active size and percentage values.
                </AlertDescription>
              </Alert>
              <div className="flex items-center gap-2">
                <Button variant="outline" onClick={handleDiscard} disabled={isSaving || jobState === 'running'}>Discard</Button>
                <Button onClick={handleSave} disabled={isSaving || jobState === 'running'}>
                  {isSaving ? 'Saving...' : jobState === 'running' ? 'Recalculating...' : 'Save'}
                </Button>
              </div>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Explainer Modal */}
      <ExplainerModal
        open={showExplainer}
        onOpenChange={setShowExplainer}
        type={type}
        integrationSystem={integrationSystem}
      />
    </>
  );
}

// Explainer Modal Component
interface ExplainerModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  type: 'capex' | 'rnd';
  integrationSystem: string;
}

function ExplainerModal({
  open,
  onOpenChange,
  type,
  integrationSystem,
}: ExplainerModalProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle>Column Explanations</DialogTitle>
          <DialogDescription>Understanding the calculation breakdown columns</DialogDescription>
        </DialogHeader>

        <ScrollArea className="max-h-[60vh] pr-4">
          <div className="space-y-6">
            {/* EPIC Column */}
            <div>
              <h3 className="font-semibold text-lg mb-2">EPIC</h3>
              <p className="text-sm text-muted-foreground">
                The EPIC identifier and title. Click on an EPIC to view its detailed information.
              </p>
            </div>

            {/* Relative EPIC Size */}
            <div>
              <h3 className="font-semibold text-lg mb-2">Relative EPIC Size</h3>
              <p className="text-sm text-muted-foreground mb-3">
                The weight used for each EPIC in the weighted average calculation. Duration (days) is the default,
                objective weight derived from the EPIC&apos;s lifespan. A manual override can be entered if needed.
              </p>

              <div className="space-y-3 ml-4">
                <div>
                  <h4 className="font-medium text-sm mb-1">Duration (days)</h4>
                  <p className="text-sm text-muted-foreground">
                    The number of days between the EPIC&apos;s Active and Closed dates (duration_created_to_closed).
                    This is an objective, audit-defensible measure consistent across all projects. Click to select
                    as the active size source. Highlighted in green when active.
                  </p>
                </div>

                <div>
                  <h4 className="font-medium text-sm mb-1">Manual Override</h4>
                  <p className="text-sm text-muted-foreground">
                    Enter a custom size value to override the duration. When populated and greater than 0, this value
                    is used instead of duration. Highlighted in amber when active.
                  </p>
                </div>
              </div>
            </div>

            {/* Percentage */}
            <div>
              <h3 className="font-semibold text-lg mb-2">Percentage</h3>
              <p className="text-sm text-muted-foreground mb-3">
                The {type === 'capex' ? 'CapEx' : 'R&D'} percentage for the EPIC. Click any cell to select it as the
                active value. The selected cell will be highlighted in green. When a manual value is entered,
                the cell turns amber (yellow) to indicate a manual override.
              </p>

              <div className="space-y-3 ml-4">
                <div>
                  <h4 className="font-medium text-sm mb-1">{integrationSystem}</h4>
                  <p className="text-sm text-muted-foreground">
                    The percentage value from {integrationSystem} data, if available.
                  </p>
                </div>

                <div>
                  <h4 className="font-medium text-sm mb-1">System Guidance</h4>
                  <p className="text-sm text-muted-foreground">
                    The system generated % based on analysis of all work items against qualifying criteria. This is the current active value being used.
                  </p>
                </div>

                <div>
                  <h4 className="font-medium text-sm mb-1">Manual Override</h4>
                  <p className="text-sm text-muted-foreground">
                    Enter a custom percentage value (0-100). Click the input field to select this as the active value.
                    Highlighted in amber when a manual value is entered, consistent with the Manual Override behavior
                    in the Relative EPIC Size column.
                  </p>
                </div>
              </div>
            </div>

            {/* Contribution */}
            <div>
              <h3 className="font-semibold text-lg mb-2">Contribution</h3>
              <p className="text-sm text-muted-foreground">
                The weighted contribution of this EPIC to the total {type === 'capex' ? 'CapEx' : 'R&D'} percentage.
                Calculated as: (Active Size × Selected Percentage) ÷ 100
              </p>
            </div>

            {/* Calculation Formula */}
            <div className="bg-muted/50 p-4 rounded-lg">
              <h3 className="font-semibold text-lg mb-2">Total Calculation</h3>
              <p className="text-sm text-muted-foreground mb-2">
                The total {type === 'capex' ? 'CapEx' : 'R&D'} percentage is calculated as a weighted average:
              </p>
              <div className="bg-background p-3 rounded border font-mono text-sm">
                Total = (Sum of all Contributions) ÷ (Sum of all Sizes) × 100
              </div>
            </div>

            {/* Special Case */}
            <div className="bg-amber-50 border border-amber-200 p-4 rounded-lg">
              <h3 className="font-semibold text-lg mb-2">Single EPIC Special Case</h3>
              <p className="text-sm text-muted-foreground">
                When only one EPIC exists in the calculation, its size is automatically set to 1 to ensure the total
                percentage matches the EPIC&apos;s percentage value exactly.
              </p>
            </div>
          </div>
        </ScrollArea>

        <DialogFooter>
          <Button onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
